import React, { useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { returnInputFieldLength } from "../../../utils/commonFunctions";
import moment from "moment";
import LinkDetails from "./LinkDetails";
import "./LinkUploadModal.scss";
import { AddDocumentLinkPayloadType } from "../../../commonTypes/listing";
import { DocumentsLinkType } from "../../../redux/listing/listingTypes";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/rootReducers";
import {
  getJobsListAction,
  getUserMapedClientJobListAction,
} from "../../../redux/jobs";

type AppProps = {
  modal: boolean;
  toggleModal: () => void;
  characterLength?: number;
  showInputBox?: boolean;
  isAdmin?: boolean;
  client_code?: number;
  company_name?: string;
  data?: any;
  setOpenStatusValue?: (data: string) => void;
  setOpenStatus?: (data: string) => void;
  handleAddDocumentLink?: (data: AddDocumentLinkPayloadType) => void;
  documentsLinkList: [] | DocumentsLinkType[];
  commentFetching?: boolean;
  title?: string;
  filterStatus?: string;
  endDate?: string;
  jobType?: string;
  status?: string;
  sortByClientCode?: string;
  clientName?: string;
  searchData?: string;
  jobPriority?: any;
  setOpenJobOffset?: React.Dispatch<React.SetStateAction<number>>;
  openJobOffset?: number;
};

const LinkUploadModal = ({
  title,
  modal,
  toggleModal,
  data,
  characterLength,
  isAdmin,
  showInputBox,
  client_code,
  company_name,
  documentsLinkList,
  handleAddDocumentLink,
  commentFetching,
  filterStatus,
  endDate,
  jobType,
  status,
  sortByClientCode,
  clientName,
  searchData,
  jobPriority,
  openJobOffset,
  setOpenJobOffset,
}: AppProps) => {
  const dispatch = useDispatch();
  const [addLinkText, setAddLinkText] = useState<string>("");
  const [addNotes, setAddNotes] = useState<string>("");
  const [addLinkError, setAddLinkError] = useState<string>("");
  const [isShow, setisShow] = useState(modal);
  const [refresh, setRefresh] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(20);

  const token = useSelector((state: RootState) => state.auth.token);

  let todayDate = moment(new Date()).format("YYYY-MM-DD");

  const finalDate = data?.target_end_date;

  const jobProgress = "job-Progress-Open-Jobs";
  const jobTracker = "job-Tracker-List";
  const openJobList = "assigned-Client-Open-JobList";

  let targetEndDate = <span>{data?.target_end_date}</span>;
  if (
    !isAdmin &&
    data?.job_status === "Open" &&
    moment(finalDate).isBefore(todayDate)
  ) {
    targetEndDate = (
      <span className="status-Rejected">{data?.target_end_date}</span>
    );
  } else if (isAdmin && moment(finalDate).isBefore(todayDate)) {
    targetEndDate = (
      <span className="status-Rejected">{data?.target_end_date}</span>
    );
  }

  const handleAddLinkTextChange = (e: any) => {
    setAddLinkText(e.target.value);

    if (addLinkError) {
      setAddLinkError("");
    }
  };

  const handleAddNoteTextChange = (e: any) => {
    setAddNotes(e.target.value);
  };

  const handleRefreshList = () => {
    toggleModal();
    if (token && client_code) {
      setisShow(!isShow);
      if (refresh) {
        handleAddJobLinkSuccess();
      }
    }
  };

  const renderDocumentLinks = () => {
    let finalDocumentLinks = documentsLinkList?.map(
      (document: DocumentsLinkType) => {
        let finalName = !document?.first_name?.length
          ? `${document?.company_name?.slice(0, 25)}...`
          : `${document.first_name} ${document?.last_name}`;

        return (
          <LinkDetails
            key={document?.job_link_id}
            commenterName={finalName}
            linkNote={document?.description}
            linkText={document?.link}
            dateTime={document?.created_at}
            data={document}
            client_code={client_code}
            company_name={!!company_name ? company_name : ""}
          />
        );
      }
    );

    return finalDocumentLinks;
  };

  const isLinkValidated = (link: string) => {
    return link?.includes("https://finac.akssai.com/file-manager/");
  };

  const handleSubmitDocumentLink = (e: any) => {
    e.preventDefault();
    if (addLinkText?.length) {
      const isValidated = isLinkValidated(addLinkText);

      if (
        // commented to remove validation for checking emailers.
        !isValidated
        // false
      ) {
        setAddLinkError(
          "Link must start with https://finac.akssai.com/file-manager/"
        );
      } else {
        let finalData: AddDocumentLinkPayloadType = {
          link: addLinkText,
          description: addNotes,
          job_id: data?._id,
        };
        setAddLinkError("");
        handleAddDocumentLink?.(finalData);
        setAddLinkText("");
        setAddNotes("");
        setRefresh(true);
      }
    } else {
      setAddLinkError("Required!");
    }
  };

  const handleAddJobLinkSuccess = () => {
    switch (title) {
      case jobProgress:
        dispatch(
          getJobsListAction({
            token,
            limit,
            offset,
            filterStatus,
            search_term: searchData,
            endDate,
            jobType,
            status,
            sortByClientCode,
            clientName,
            jobPriority: jobPriority?.value,
          })
        );
        break;
      case jobTracker:
        dispatch(
          getJobsListAction({ token, clientCode: client_code, status: "1" })
        );
        break;
      case openJobList:
        <>
          {openJobOffset === 0 ? (
            dispatch(
              getUserMapedClientJobListAction({
                token,
                status: "Open",
                filterStatus,
                endDate,
                jobType,
                sortByClientCode,
                clientName,
                search_term: searchData,
                jobPriority: jobPriority?.value,
                limit,
                offset,
              })
            )
          ) : (
            <>
              {window.scrollTo(0, 0)}
              {setOpenJobOffset && setOpenJobOffset(0)}
            </>
          )}
        </>;
        break;
      default:
        dispatch(getJobsListAction({ token }));
        break;
    }
  };

  return (
    <>
      <Modal
        isOpen={isShow}
        toggle={() => setisShow(true)}
        className="modal-styles"
        size="lg"
      >
        <ModalHeader toggle={handleRefreshList} className="p-4">
          <span
            style={{ fontSize: "17px" }}
          >{`${company_name}(${client_code})`}</span>
          <div className="mt-2">
            <span
              style={{ fontSize: "14px" }}
            >{`${data?.job_type} (${data?.job_id})`}</span>
            {!isAdmin && data?.job_status && (
              <span
                className={`${
                  data?.job_status === "Open" ? "status-open" : "status-close"
                }`}
              >
                {data?.job_status}
              </span>
            )}
            <span style={{ fontSize: "14px", marginLeft: "20px" }}>
              Target End Date: {targetEndDate}{" "}
            </span>
          </div>
        </ModalHeader>
        {showInputBox && (
          <ModalBody className="p-4">
            <div className="row">
              <div className="col-md-12">
                <span style={{ fontSize: "18px", fontWeight: 500 }}>
                  Attach File Link
                </span>
              </div>
            </div>
            <form>
              <div className="form-group mt-3">
                <label
                  htmlFor="commentBox"
                  className={addLinkText ? "active" : ""}
                >
                  Add Link*
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    !!addLinkError ? "error-border" : null
                  }`}
                  value={addLinkText}
                  autoFocus
                  onChange={(e) => handleAddLinkTextChange(e)}
                  maxLength={characterLength}
                  onKeyPress={(e: any) =>
                    e.key === "Enter" ? handleSubmitDocumentLink(e) : null
                  }
                  disabled={data?.job_status === "Closed"}
                  placeholder="https://finac.akssai.com/file-manager/..."
                />
                {!!addLinkError && (
                  <small className="text-danger">{addLinkError}</small>
                )}
              </div>
            </form>
            <form>
              <div className="form-group mt-3">
                <label
                  htmlFor="commentBox"
                  className={addNotes ? "active" : ""}
                >
                  Note(Optional)
                </label>
                <textarea
                  className={`form-control`}
                  value={addNotes}
                  onChange={(e) => handleAddNoteTextChange(e)}
                  maxLength={250}
                  onKeyPress={(e: any) =>
                    e.key === "Enter" ? handleSubmitDocumentLink(e) : null
                  }
                  disabled={data?.job_status === "Closed"}
                />
                <div className="input-length text-right mt-1">
                  {returnInputFieldLength(addNotes.length, 250)}
                </div>
              </div>
            </form>
            <div className="text-right">
              <span className="pointer" onClick={handleRefreshList}>
                Cancel
              </span>
              <Button
                color="primary ml-3"
                onClick={(e: any) => {
                  handleSubmitDocumentLink(e);
                }}
                disabled={!addLinkText.length || commentFetching}
              >
                {commentFetching ? "Adding..." : "Add"}
              </Button>
            </div>

            <div className="mt-2" style={{ fontSize: "13px" }}>
              <p className="text-danger m-0 mb-1">Please Note:</p>
              <p className="m-0 mb-1 text-danger">
                1. Please do not share the file URL directly to anyone (on email
                or WhatsApp, etc.). Please do add the link ONLY in the text-box
                above to have a seamless experience because of security reasons.{" "}
              </p>
              <p className="text-danger">
                2. Make sure that there is at least one folder in the path
                otherwise, it will give error.
              </p>
            </div>
          </ModalBody>
        )}
        <ModalFooter className="p-4" style={{ backgroundColor: "#f7f7f8" }}>
          <div className="w-100 comment-area">
            <p style={{ fontSize: "18px", fontWeight: 500 }}>
              Attached File Links
            </p>
            {renderDocumentLinks()}
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default LinkUploadModal;
